import { useTranslation } from "react-i18next";
import type { UnassignedReasonSummary } from "@/features/routes/lib/unassignedBreakdown";

interface Props {
  summaries: UnassignedReasonSummary[];
  /** BUG-691: the build's warnings, already worded — listed before the reasons. */
  warnings?: string[];
}

/**
 * Description of the build-result toast (BUG-676): one block per reason, each
 * listing a few addresses with an order count («…, г. Толочин ×5») and an
 * «и ещё N заказов» tail.
 *
 * No colour classes on purpose — the text inherits the toast flavour's own
 * colour (warning / error), so the body can never disagree with the outcome.
 */
export function BuildResultToastBody({ summaries, warnings = [] }: Props) {
  const { t } = useTranslation();

  return (
    <div className="max-h-72 space-y-2 overflow-y-auto">
      {warnings.map((warning) => (
        <p key={warning} data-testid="build-result-warning">
          {warning}
        </p>
      ))}
      {summaries.map((summary) => (
        <div key={summary.reason} data-testid="build-result-reason">
          <p className="font-semibold">
            {summary.label}
            <span className="ml-1 font-normal">({summary.total})</span>
          </p>
          {summary.lines.length > 0 ? (
            <ul className="space-y-0.5">
              {summary.lines.map((line) => (
                <li key={line.label}>
                  {line.count > 1 ? `${line.label} ×${line.count}` : line.label}
                  {line.detail ? ` — ${line.detail}` : null}
                  {/* BUG-685: this date's copy of a permanent order is gone. */}
                  {line.fromPermanent ? (
                    <span className="block">
                      {t("routes.buildAlert.permanentCopyRemoved")}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          {summary.hiddenCount > 0 ? (
            <p>
              {t("routes.buildAlert.moreOrders", {
                count: summary.hiddenCount,
              })}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}
